import { showError, showWarning } from '@/common/functions.js'

export const ApiErrorType = {
  NETWORK:    'network',
  FORBIDDEN:  'forbidden',
  NOT_FOUND:  'not_found',
  VALIDATION: 'validation',
  RATE_LIMIT: 'rate_limit',
  SERVER:     'server',
  UNKNOWN:    'unknown',
}

export function classifyError(error) {
  if (!error?.response) return { type: ApiErrorType.NETWORK, status: null }
  const { status } = error.response
  if (status === 401 || status === 403) return { type: ApiErrorType.FORBIDDEN, status }
  if (status === 404)                   return { type: ApiErrorType.NOT_FOUND, status }
  if (status === 400 || status === 422) return { type: ApiErrorType.VALIDATION, status }
  if (status === 429)                   return { type: ApiErrorType.RATE_LIMIT, status }
  if (status >= 500)                    return { type: ApiErrorType.SERVER, status }
  return { type: ApiErrorType.UNKNOWN, status }
}

// Recursively collects human-readable error strings from nested DRF validation responses.
// Handles: string | string[] | {detail} | {message} | {non_field_errors:[]} | {field:[msg,...]}
// and nested shapes like {options: [{legs: [{depart_from: ["Required."]}]}]}
function collectErrors(data, prefix = '') {
  const msgs = []
  if (!data) return msgs
  if (typeof data === 'string') { msgs.push(prefix ? `${prefix}: ${data}` : data); return msgs }
  if (Array.isArray(data)) {
    data.forEach((item, i) => {
      if (typeof item === 'string') {
        msgs.push(prefix ? `${prefix}: ${item}` : item)
      } else if (item && typeof item === 'object') {
        msgs.push(...collectErrors(item, prefix))
      }
    })
    return msgs
  }
  if (typeof data === 'object') {
    for (const [key, val] of Object.entries(data)) {
      const label = key.replace(/_/g, ' ')
      const childPrefix = prefix ? `${prefix} → ${label}` : label
      msgs.push(...collectErrors(val, childPrefix))
    }
  }
  return msgs
}

export function extractMessage(error, fallback = 'An unexpected error occurred.') {
  const data = error?.response?.data
  if (!data) return error?.message || fallback
  if (typeof data === 'string') return data
  if (data.detail) return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail)
  if (data.message) return data.message
  if (Array.isArray(data.non_field_errors)) return data.non_field_errors[0] || fallback
  const errors = collectErrors(data)
  return errors.length > 0 ? errors.join('\n') : fallback
}

// Single function every store catch block calls.
// Returns { type, status, message } so callers can branch on type if needed.
export function handleApiError(error, { title = 'Error', silent = false, fallback } = {}) {
  const { type, status } = classifyError(error)
  const message = extractMessage(error, fallback)

  if (!silent) {
    // Validation errors use warning toast (softer — it's user input feedback)
    type === ApiErrorType.VALIDATION
      ? showWarning(title, message)
      : showError(title, message)
  }

  console.error(`[API ${status ?? 'NET'}] ${title}`, { type, error })
  return { type, status, message }
}

export const isNotFound   = (e) => classifyError(e).type === ApiErrorType.NOT_FOUND
export const isValidation = (e) => classifyError(e).type === ApiErrorType.VALIDATION
export const isForbidden  = (e) => classifyError(e).type === ApiErrorType.FORBIDDEN
export const isNetwork    = (e) => classifyError(e).type === ApiErrorType.NETWORK
